import { innovorderApi, ApiTagTypes } from '@/services/innovorder';
import type { ApiResponse } from '@/services/innovorder/request.types';

import type { PickupScheduleSlot } from '@/services/innovorder/restaurant/restaurant.type';

import type {
    PromotionalBanner,
    GetPromotionalBannersPayload,
    CreatePromotionalBannerPayload,
    UpdatePromotionalBannerPayload,
    TogglePromotionalBannerPayload,
    ToggleRestaurantBrandBannerPayload,
    DeletePromotionalBannerPayload,
    BrandPromotionalBanner,
    GetBrandPromotionalBannersPayload,
    CreateBrandPromotionalBannerPayload,
    UpdateBrandPromotionalBannerPayload,
    ToggleBrandPromotionalBannerPayload,
    DeleteBrandPromotionalBannerPayload,
    DuplicatePromotionalBannerPayload,
    GetBrandPromotionalBannerSettingsPayload,
    BrandPromotionalBannerSettings,
    UpdateBrandPromotionalBannerSettingsPayload,
    BrandPromotionalBannerTarget,
    GetBrandPromotionalBannerTargetsPayload,
    GetBrandPickupSchedulesPayload,
} from './promotionalBanner.type';

const promotionalBannerApi = innovorderApi.injectEndpoints({
    endpoints: (build) => ({
        getPromotionalBanners: build.query<PromotionalBanner[], GetPromotionalBannersPayload>({
            query: ({ token, restaurantId, sort }) => ({
                url: `/promotional_banners/restaurant/${restaurantId}`,
                method: 'GET',
                headers: { authorization: `Bearer ${token}` },
                params: sort ? { sort } : undefined,
            }),
            transformResponse: (response: ApiResponse<PromotionalBanner[]>) => response.data,
            providesTags: [ApiTagTypes.PromotionalBanner],
        }),
        createPromotionalBanner: build.mutation<PromotionalBanner, CreatePromotionalBannerPayload>({
            query: ({ token, restaurantId, ...body }) => ({
                url: `/promotional_banners/restaurant/${restaurantId}`,
                method: 'POST',
                headers: { authorization: `Bearer ${token}` },
                body,
            }),
            transformResponse: (response: ApiResponse<PromotionalBanner>) => response.data,
            invalidatesTags: [ApiTagTypes.PromotionalBanner],
        }),
        updatePromotionalBanner: build.mutation<PromotionalBanner, UpdatePromotionalBannerPayload>({
            query: ({ token, promotionalBannerId, ...body }) => ({
                url: `/promotional_banners/${promotionalBannerId}`,
                method: 'PUT',
                headers: { authorization: `Bearer ${token}` },
                body,
            }),
            transformResponse: (response: ApiResponse<PromotionalBanner>) => response.data,
            invalidatesTags: [ApiTagTypes.PromotionalBanner],
        }),
        togglePromotionalBanner: build.mutation<PromotionalBanner, TogglePromotionalBannerPayload>({
            query: ({ token, promotionalBannerId, ...body }) => ({
                url: `/promotional_banners/${promotionalBannerId}/toggle`,
                method: 'PATCH',
                headers: { authorization: `Bearer ${token}` },
                body,
            }),
            transformResponse: (response: ApiResponse<PromotionalBanner>) => response.data,
            invalidatesTags: [ApiTagTypes.PromotionalBanner],
        }),
        toggleRestaurantBrandBanner: build.mutation<PromotionalBanner, ToggleRestaurantBrandBannerPayload>({
            query: ({ token, promotionalBannerId, restaurantId, ...body }) => ({
                url: `/promotional_banners/${promotionalBannerId}/restaurant/${restaurantId}/toggle`,
                method: 'PATCH',
                headers: { authorization: `Bearer ${token}` },
                body,
            }),
            transformResponse: (response: ApiResponse<PromotionalBanner>) => response.data,
            invalidatesTags: [ApiTagTypes.PromotionalBanner],
        }),
        deletePromotionalBanner: build.mutation<void, DeletePromotionalBannerPayload>({
            query: ({ token, promotionalBannerId }) => ({
                url: `/promotional_banners/${promotionalBannerId}`,
                method: 'DELETE',
                headers: { authorization: `Bearer ${token}` },
            }),
            invalidatesTags: [ApiTagTypes.PromotionalBanner],
        }),
        getBrandPromotionalBanners: build.query<BrandPromotionalBanner[], GetBrandPromotionalBannersPayload>({
            query: ({ token, brandId, sort }) => ({
                url: `/brands/${brandId}/promotional_banners`,
                method: 'GET',
                headers: { authorization: `Bearer ${token}` },
                params: sort ? { sort } : undefined,
            }),
            transformResponse: (response: ApiResponse<BrandPromotionalBanner[]>) => response.data,
            providesTags: [ApiTagTypes.BrandPromotionalBanner],
        }),
        createBrandPromotionalBanner: build.mutation<BrandPromotionalBanner, CreateBrandPromotionalBannerPayload>({
            query: ({ token, brandId, ...body }) => ({
                url: `/brands/${brandId}/promotional_banners`,
                method: 'POST',
                headers: { authorization: `Bearer ${token}` },
                body,
            }),
            transformResponse: (response: ApiResponse<BrandPromotionalBanner>) => response.data,
            invalidatesTags: [ApiTagTypes.BrandPromotionalBanner, ApiTagTypes.PromotionalBanner],
        }),
        updateBrandPromotionalBanner: build.mutation<BrandPromotionalBanner, UpdateBrandPromotionalBannerPayload>({
            query: ({ token, brandId, promotionalBannerId, ...body }) => ({
                url: `/brands/${brandId}/promotional_banners/${promotionalBannerId}`,
                method: 'PUT',
                headers: { authorization: `Bearer ${token}` },
                body,
            }),
            transformResponse: (response: ApiResponse<BrandPromotionalBanner>) => response.data,
            invalidatesTags: [ApiTagTypes.BrandPromotionalBanner, ApiTagTypes.PromotionalBanner],
        }),
        toggleBrandPromotionalBanner: build.mutation<BrandPromotionalBanner, ToggleBrandPromotionalBannerPayload>({
            query: ({ token, brandId, promotionalBannerId, ...body }) => ({
                url: `/brands/${brandId}/promotional_banners/${promotionalBannerId}/toggle`,
                method: 'PATCH',
                headers: { authorization: `Bearer ${token}` },
                body,
            }),
            transformResponse: (response: ApiResponse<BrandPromotionalBanner>) => response.data,
            invalidatesTags: [ApiTagTypes.BrandPromotionalBanner, ApiTagTypes.PromotionalBanner],
        }),
        deleteBrandPromotionalBanner: build.mutation<void, DeleteBrandPromotionalBannerPayload>({
            query: ({ token, brandId, promotionalBannerId }) => ({
                url: `/brands/${brandId}/promotional_banners/${promotionalBannerId}`,
                method: 'DELETE',
                headers: { authorization: `Bearer ${token}` },
            }),
            invalidatesTags: [ApiTagTypes.BrandPromotionalBanner, ApiTagTypes.PromotionalBanner],
        }),
        duplicatePromotionalBanner: build.mutation<PromotionalBanner, DuplicatePromotionalBannerPayload>({
            query: ({ token, promotionalBannerId, restaurantId }) => ({
                url: `/promotional_banners/${promotionalBannerId}/duplicate`,
                method: 'POST',
                headers: { authorization: `Bearer ${token}` },
                body: { restaurantId },
            }),
            transformResponse: (response: ApiResponse<PromotionalBanner>) => response.data,
            invalidatesTags: [ApiTagTypes.PromotionalBanner],
        }),
        getBrandPromotionalBannerSettings: build.query<
            BrandPromotionalBannerSettings,
            GetBrandPromotionalBannerSettingsPayload
        >({
            query: ({ token, brandId }) => ({
                url: `/brands/${brandId}/promotional_banners_settings`,
                method: 'GET',
                headers: { authorization: `Bearer ${token}` },
            }),
            transformResponse: (response: ApiResponse<BrandPromotionalBannerSettings>) => response.data,
            providesTags: [ApiTagTypes.BrandPromotionalBannerSettings],
        }),
        updateBrandPromotionalBannerSettings: build.mutation<
            BrandPromotionalBannerSettings,
            UpdateBrandPromotionalBannerSettingsPayload
        >({
            query: ({ token, brandId, ...body }) => ({
                url: `/brands/${brandId}/promotional_banners_settings`,
                method: 'PUT',
                headers: { authorization: `Bearer ${token}` },
                body,
            }),
            transformResponse: (response: ApiResponse<BrandPromotionalBannerSettings>) => response.data,
            invalidatesTags: [ApiTagTypes.BrandPromotionalBannerSettings],
        }),
        getBrandPromotionalBannerTargets: build.query<
            BrandPromotionalBannerTarget[],
            GetBrandPromotionalBannerTargetsPayload
        >({
            query: ({ token, brandId }) => ({
                url: `/brands/${brandId}/promotional_banner_targets`,
                method: 'GET',
                headers: { authorization: `Bearer ${token}` },
            }),
            transformResponse: (response: ApiResponse<BrandPromotionalBannerTarget[]>) => response.data,
            providesTags: [ApiTagTypes.BrandPromotionalBanner],
        }),
        getBrandPickupSchedules: build.query<Record<number, PickupScheduleSlot[]>, GetBrandPickupSchedulesPayload>({
            query: ({ token, brandId }) => ({
                url: `/brands/${brandId}/pickup_schedules`,
                method: 'GET',
                headers: { authorization: `Bearer ${token}` },
            }),
            transformResponse: (response: ApiResponse<Record<number, PickupScheduleSlot[]>>) => response.data,
            providesTags: [ApiTagTypes.BrandPromotionalBanner],
        }),
    }),
});

export const {
    useGetPromotionalBannersQuery,
    useLazyGetPromotionalBannersQuery,
    useCreatePromotionalBannerMutation,
    useUpdatePromotionalBannerMutation,
    useTogglePromotionalBannerMutation,
    useToggleRestaurantBrandBannerMutation,
    useDeletePromotionalBannerMutation,
    useGetBrandPromotionalBannersQuery,
    useLazyGetBrandPromotionalBannersQuery,
    useCreateBrandPromotionalBannerMutation,
    useUpdateBrandPromotionalBannerMutation,
    useToggleBrandPromotionalBannerMutation,
    useDeleteBrandPromotionalBannerMutation,
    useDuplicatePromotionalBannerMutation,
    useGetBrandPromotionalBannerSettingsQuery,
    useUpdateBrandPromotionalBannerSettingsMutation,
    useGetBrandPromotionalBannerTargetsQuery,
    useGetBrandPickupSchedulesQuery,
} = promotionalBannerApi;
